/**
 * RestoID — legal document page (1:1 with the Claude Design).
 * ---------------------------------------------------------------------------
 * Faithful port of the design's Privacy.dc.html / Consent.dc.html: a standalone
 * full-width document (own sticky header with the RestoID shield + "Ко входу",
 * a fixed top aurora glow, an 784px reading column, and the legal text from
 * document-content.ts). Rendered OUTSIDE AppLayout; the global auth chrome,
 * aurora and boot loader are hidden while a `.restoid-doc-page` is mounted
 * (see restoid.css). Russian-only, exactly like the design.
 */
import { type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';

import { C, FONT } from '../components/restoid';
import { RESTOID_LOGO_SVG } from '../logo';

import { DOCUMENTS, type DocId, type PrivacyBlock } from './document-content';

/* Color is themeable, never frozen: `C.*` wherever a shared RestoID token fits
   exactly, and `var(--rid-doc-*, <literal>)` for the type alphas, rules and lime
   washes this page owns alone.

   All sixteen `--rid-doc-*` tokens now ship in BOTH themes (restoid.css:167-186 and
   :861-877), so no var() below reaches its fallback and the literals are dead code.
   They are kept only as a legible default if a token is ever dropped — do NOT treat
   them as the current value, and do NOT "simplify" a var() back to its literal. Two
   have deliberately diverged since they landed:

     --rid-doc-footer-note  literal 0.40 -> ships 0.48   (restoid.css:176)
     --rid-doc-check-line   literal 0.40 -> ships 0.46   (restoid.css:185)

   The first is not cosmetic. On #0a0b0d the literal composites to #67686a, 3.52:1 —
   BELOW WCAG AA for small text. The shipped 0.48 gives #7a7b7c, 4.64:1. Restoring the
   literal would quietly push the footer's copyright line under the threshold. */

const SHELL: CSSProperties = {
  maxWidth: 1148,
  marginLeft: 'auto',
  marginRight: 'auto',
  paddingLeft: 28,
  paddingRight: 28,
};
const COL: CSSProperties = { maxWidth: 784, marginLeft: 'auto', marginRight: 'auto' };

/** Fixed lime aurora glow anchored to the top of the page. */
const Aurora = () => (
  <div
    style={{
      position: 'fixed',
      inset: '0 0 auto 0',
      height: 640,
      pointerEvents: 'none',
      overflow: 'hidden',
    }}
  >
    <div
      className="restoid-doc-aurora"
      style={{
        position: 'absolute',
        top: '-46vh',
        left: '50%',
        width: 'min(1100px,100vw)',
        height: 800,
        marginLeft: 'calc(min(1100px,100vw) * -0.5)',
        transformOrigin: '50% 0%',
        willChange: 'opacity,transform',
        background:
          'radial-gradient(ellipse 56% 52% at 50% 26%, var(--rid-doc-aurora, rgba(176,207,47,0.16)), transparent 68%)',
        animation: 'restoDocGlow 16s ease-in-out infinite',
      }}
    />
  </div>
);

/** Sticky top bar: RestoID shield + wordmark, and a "Ко входу" button. */
const DocHeader = () => {
  const navigate = useNavigate();
  return (
    <div
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 20,
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        background: 'var(--rid-doc-header-bg, rgba(10,11,13,0.72))',
        borderBottom: '1px solid var(--rid-doc-line, rgba(255,255,255,0.07))',
      }}
    >
      <div
        style={{
          ...SHELL,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: 64,
        }}
      >
        <span
          role="img"
          aria-label="RestoID"
          style={{ display: 'inline-block', height: 23 }}
          // eslint-disable-next-line react/no-danger -- static, build-time logo markup
          dangerouslySetInnerHTML={{ __html: RESTOID_LOGO_SVG }}
        />
        <button
          type="button"
          onClick={() => navigate(-1)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
            cursor: 'pointer',
            padding: '8px 15px 8px 11px',
            borderRadius: 10,
            background: 'var(--rid-light, rgba(255,255,255,0.04))',
            border: '1px solid var(--rid-doc-btn-line, rgba(255,255,255,0.09))',
            color: 'var(--rid-doc-btn-ink, rgba(243,244,245,0.7))',
            font: `500 13px/1 ${FONT}`,
          }}
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M15 6l-6 6 6 6" />
          </svg>
          <span>Ко входу</span>
        </button>
      </div>
    </div>
  );
};

/** Lime "Правовые документы · RestoID" eyebrow. */
const Eyebrow = () => (
  <div
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 9,
      color: C.brand,
      font: `600 12px/1 ${FONT}`,
      letterSpacing: '0.16em',
      textTransform: 'uppercase',
    }}
  >
    <span style={{ width: 18, height: 1.5, background: C.brand, display: 'inline-block' }} />
    Правовые документы · RestoID
  </div>
);

const Footer = ({ date, marginTop }: { date: string; marginTop: number }) => (
  <footer
    style={{
      marginTop,
      paddingTop: 30,
      borderTop: '1px solid var(--rid-doc-footer-line, rgba(255,255,255,0.08))',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 18,
      flexWrap: 'wrap',
    }}
  >
    <span
      style={{
        color: 'var(--rid-doc-footer-date, rgba(243,244,245,0.6))',
        font: `500 14px/1.4 ${FONT}`,
      }}
    >
      {date}
    </span>
    <span
      style={{
        color: 'var(--rid-doc-footer-note, rgba(243,244,245,0.4))',
        font: `400 13px/1.4 ${FONT}`,
      }}
    >
      © 2026 RestoCapital · ООО «РТК»
    </span>
  </footer>
);

const Paragraph = ({
  text,
  fontSize,
  lineHeight,
}: {
  text: string;
  fontSize: number;
  lineHeight: number;
}) => (
  <p
    style={{
      margin: '18px 0 0',
      color: 'var(--rid-doc-body, rgba(243,244,245,0.74))',
      font: `400 ${fontSize}px/${lineHeight} ${FONT}`,
    }}
  >
    {text}
  </p>
);

/** Privacy body — numbered sections (РАЗДЕЛ NN) with paragraphs and bullet lists. */
const PrivacySections = ({ blocks }: { blocks: PrivacyBlock[] }) => {
  let section = 0;
  return (
    <>
      {blocks.map((block, i) => {
        if ('h' in block) {
          section += 1;
          const num = `${section < 10 ? '0' : ''}${section}`;
          const htitle = block.h.replace(/^\d+\.\s*/, '');
          return (
            <div
              // eslint-disable-next-line react/no-array-index-key -- static content
              key={i}
              style={{
                marginTop: 56,
                paddingTop: 32,
                borderTop: '1px solid var(--rid-doc-line, rgba(255,255,255,0.07))',
              }}
            >
              <div
                style={{
                  color: C.brand,
                  font: `700 12px/1 ${FONT}`,
                  letterSpacing: '0.16em',
                  marginBottom: 12,
                }}
              >
                РАЗДЕЛ {num}
              </div>
              <h2
                style={{
                  margin: 0,
                  scrollMarginTop: 96,
                  color: C.text,
                  font: `700 25px/1.3 ${FONT}`,
                  letterSpacing: '-0.02em',
                }}
              >
                {htitle}
              </h2>
            </div>
          );
        }
        if ('ul' in block) {
          return (
            <ul
              // eslint-disable-next-line react/no-array-index-key -- static content
              key={i}
              style={{
                margin: '14px 0 0',
                padding: 0,
                listStyle: 'none',
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}
            >
              {block.ul.map((it, j) => (
                <li
                  // eslint-disable-next-line react/no-array-index-key -- static content
                  key={j}
                  style={{
                    position: 'relative',
                    paddingLeft: 24,
                    color: 'var(--rid-doc-body, rgba(243,244,245,0.74))',
                    font: `400 16.5px/1.7 ${FONT}`,
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      left: 2,
                      top: 11,
                      width: 7,
                      height: 7,
                      borderRadius: 2,
                      background: 'var(--rid-doc-bullet, rgba(176,207,47,0.7))',
                    }}
                  />
                  {it}
                </li>
              ))}
            </ul>
          );
        }
        // eslint-disable-next-line react/no-array-index-key -- static content
        return <Paragraph key={i} text={block.p} fontSize={16.5} lineHeight={1.78} />;
      })}
    </>
  );
};

const CheckTile = () => (
  <span
    style={{
      flex: '0 0 auto',
      marginTop: 1,
      width: 18,
      height: 18,
      borderRadius: 6,
      background: 'var(--rid-doc-check-tile, rgba(176,207,47,0.16))',
      border: '1px solid var(--rid-doc-check-line, rgba(176,207,47,0.4))',
      display: 'grid',
      placeItems: 'center',
      color: C.brand,
    }}
  >
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 12.5l4.5 4.5L19 7" />
    </svg>
  </span>
);

const DocumentPage = ({ docId }: { docId: DocId }) => {
  const doc = DOCUMENTS[docId];

  return (
    <div
      className="restoid-doc-page"
      style={{ position: 'relative', minHeight: '100vh', background: C.page, fontFamily: FONT }}
    >
      <Aurora />
      <DocHeader />

      {doc.kind === 'privacy' ? (
        <>
          <div style={{ ...SHELL, position: 'relative', paddingTop: 72, paddingBottom: 46 }}>
            <div style={COL}>
              <Eyebrow />
              <h1
                style={{
                  margin: '22px 0 0',
                  color: C.text,
                  fontFamily: FONT,
                  fontWeight: 800,
                  fontSize: 'clamp(30px,4vw,46px)',
                  lineHeight: 1.13,
                  letterSpacing: '-0.03em',
                }}
              >
                {doc.title}
              </h1>
              <p
                style={{
                  margin: '24px 0 0',
                  color: 'var(--rid-doc-intro, rgba(243,244,245,0.62))',
                  font: `400 18px/1.7 ${FONT}`,
                }}
              >
                {doc.blocks[0] && 'p' in doc.blocks[0] ? doc.blocks[0].p : ''}
              </p>
            </div>
          </div>

          <div style={{ ...SHELL, position: 'relative', paddingBottom: 90 }}>
            <div style={COL}>
              <main>
                <PrivacySections blocks={doc.blocks.slice(1)} />
                <Footer date={doc.date} marginTop={72} />
              </main>
            </div>
          </div>
        </>
      ) : (
        <div style={{ ...SHELL, position: 'relative', paddingTop: 72, paddingBottom: 90 }}>
          <div style={COL}>
            <Eyebrow />
            <h1
              style={{
                margin: '22px 0 0',
                color: C.text,
                fontFamily: FONT,
                fontWeight: 800,
                fontSize: 'clamp(28px,3.6vw,44px)',
                lineHeight: 1.14,
                letterSpacing: '-0.03em',
              }}
            >
              {doc.title}
            </h1>
            <p
              style={{
                margin: '24px 0 0',
                color: 'var(--rid-doc-intro, rgba(243,244,245,0.62))',
                font: `400 18px/1.7 ${FONT}`,
              }}
            >
              {doc.intro}
            </p>

            <div
              style={{
                marginTop: 24,
                padding: '24px 26px',
                borderRadius: 16,
                background:
                  'var(--rid-doc-panel, linear-gradient(180deg, rgba(176,207,47,0.06), rgba(176,207,47,0.015)))',
                border: `1px solid ${C.tileLine}`,
              }}
            >
              <div
                style={{
                  color: 'var(--rid-doc-label, rgba(243,244,245,0.5))',
                  font: `600 11px/1 ${FONT}`,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  marginBottom: 16,
                }}
              >
                Категории данных
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                  gap: '13px 24px',
                }}
              >
                {doc.categories.map((c, i) => (
                  <div
                    // eslint-disable-next-line react/no-array-index-key -- static content
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 11,
                      color: 'var(--rid-doc-callout, rgba(243,244,245,0.82))',
                      font: `400 15.5px/1.45 ${FONT}`,
                    }}
                  >
                    <CheckTile />
                    {c}
                  </div>
                ))}
              </div>
            </div>

            <div style={{ marginTop: 8 }}>
              {doc.paras.map((p, i) => (
                <p
                  // eslint-disable-next-line react/no-array-index-key -- static content
                  key={i}
                  style={{
                    margin: '22px 0 0',
                    color: 'var(--rid-doc-body, rgba(243,244,245,0.74))',
                    font: `400 16.5px/1.8 ${FONT}`,
                  }}
                >
                  {p}
                </p>
              ))}
            </div>

            <Footer date={doc.date} marginTop={56} />
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentPage;
